import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Typography, TextField, Box, IconButton,
  Chip, Button, CircularProgress, InputAdornment, Collapse,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import {
  Check, DeleteOutline, Replay, BoltOutlined, ExpandMore, ExpandLess,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import SuccessOverlay, { isAnimEnabled } from '../Components/SuccessOverlay';
import { PageHeader, ListRow, ConfirmDialog, useToast } from '../Components/ui';
import { radius } from '../theme';

// ─── Konstanten ───────────────────────────────────────────────────────────────

const MEALS = [
  { key: 'breakfast', label: 'Frühstück',   emoji: '🌅' },
  { key: 'lunch',     label: 'Mittagessen', emoji: '☀️' },
  { key: 'dinner',    label: 'Abendessen',  emoji: '🌙' },
  { key: 'snacks',    label: 'Snacks',      emoji: '🍎' },
];

// Referenz-Einheiten für den Berechnen-Modus: "kcal pro ..." + wie die
// tatsächlich gegessene Menge dazu heißt/gemeint ist.
const UNIT_TYPES = [
  { val: '100g',    chipLabel: '100 g',   refLabel: 'kcal pro 100 g',     amountLabel: 'Gegessene Menge (g)' },
  { val: '100ml',   chipLabel: '100 ml',  refLabel: 'kcal pro 100 ml',    amountLabel: 'Gegessene Menge (ml)' },
  { val: 'portion', chipLabel: 'Portion', refLabel: 'kcal pro Portion',   amountLabel: 'Anzahl Portionen' },
];

const formatDate = (iso) => {
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y}`;
};

const computeKcal = (unitType, refKcal, amount) => {
  const refNum = Number(refKcal);
  const amtNum = Number(amount);
  if (refKcal === '' || amount === '' || !Number.isFinite(refNum) || !Number.isFinite(amtNum)) return null;
  return Math.round(unitType === 'portion' ? refNum * amtNum : (refNum / 100) * amtNum);
};

const unitDisplay = (val) => UNIT_TYPES.find((u) => u.val === val)?.chipLabel || val;

// ─── Berechnen/Gesamt-Felder ────────────────────────────────────────────────
// Gemeinsam genutzt vom Hauptformular und vom "Erneut Tracken"-Dialog:
// entweder eine feste Referenz (kcal pro 100g/100ml/Portion) + tatsächlich
// gegessene Menge → live berechnet, oder einfach die Gesamtkalorien direkt.
function CalorieCalcFields({ mode, setMode, unitType, setUnitType, refKcal, setRefKcal, amount, setAmount, totalKcal, setTotalKcal }) {
  const unitInfo = UNIT_TYPES.find((u) => u.val === unitType) || UNIT_TYPES[0];
  const computed = computeKcal(unitType, refKcal, amount);

  return (
    <>
      {/* Berechnungsschalter */}
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Chip
          label="Berechnen"
          onClick={() => setMode('calc')}
          color={mode === 'calc' ? 'primary' : 'default'}
          variant={mode === 'calc' ? 'filled' : 'outlined'}
          sx={{ fontWeight: mode === 'calc' ? 700 : 400 }}
        />
        <Chip
          label="Gesamt eintragen"
          onClick={() => setMode('total')}
          color={mode === 'total' ? 'primary' : 'default'}
          variant={mode === 'total' ? 'filled' : 'outlined'}
          sx={{ fontWeight: mode === 'total' ? 700 : 400 }}
        />
      </Box>

      {mode === 'calc' ? (
        <>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {UNIT_TYPES.map((u) => (
              <Chip
                key={u.val}
                label={u.chipLabel}
                onClick={() => setUnitType(u.val)}
                color={unitType === u.val ? 'primary' : 'default'}
                variant={unitType === u.val ? 'filled' : 'outlined'}
                sx={{ fontWeight: unitType === u.val ? 700 : 400 }}
              />
            ))}
          </Box>

          <TextField
            fullWidth
            label={unitInfo.refLabel}
            value={refKcal}
            onChange={(e) => setRefKcal(e.target.value.replace(/[^\d.,]/g, ''))}
            onFocus={(e) => e.target.select()}
            slotProps={{
              htmlInput: { inputMode: 'decimal' },
              input: { endAdornment: <InputAdornment position="end">kcal</InputAdornment> },
            }}
          />
          <TextField
            fullWidth
            label={unitInfo.amountLabel}
            value={amount}
            onChange={(e) => setAmount(e.target.value.replace(/[^\d.,]/g, ''))}
            onFocus={(e) => e.target.select()}
            slotProps={{ htmlInput: { inputMode: 'decimal' } }}
          />

          <Box sx={{ textAlign: 'center', py: 2, bgcolor: 'action.hover', borderRadius: radius.lg }}>
            <Typography variant="h4" fontWeight={800} color="primary.main" sx={{ lineHeight: 1 }}>
              {computed !== null ? computed : '–'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              kcal gesamt
            </Typography>
          </Box>
        </>
      ) : (
        <TextField
          fullWidth
          label="Kalorien (Gesamt)"
          value={totalKcal}
          onChange={(e) => setTotalKcal(e.target.value.replace(/\D/g, ''))}
          onFocus={(e) => e.target.select()}
          slotProps={{
            htmlInput: { inputMode: 'numeric', pattern: '[0-9]*' },
            input: { endAdornment: <InputAdornment position="end">kcal</InputAdornment> },
          }}
        />
      )}
    </>
  );
}

// ─── Hauptkomponente ──────────────────────────────────────────────────────────

const QuickTrackPage = () => {
  const navigate   = useNavigate();
  const location   = useLocation();

  const dateParam   = new URLSearchParams(location.search).get('date');
  const selectedDate = dateParam
    ? dateParam.split('T')[0]
    : new Date().toISOString().split('T')[0];

  const [foodName,     setFoodName]    = useState('');
  const [mode,          setMode]        = useState('calc'); // calc | total
  const [unitType,      setUnitType]    = useState('100g');
  const [refKcal,       setRefKcal]     = useState('');
  const [amount,        setAmount]      = useState('');
  const [totalKcal,     setTotalKcal]   = useState('');
  const [foodProtein,  setFoodProtein] = useState('');
  const [foodFat,      setFoodFat]     = useState('');
  const [foodCarbs,    setFoodCarbs]   = useState('');
  const [showMore,     setShowMore]    = useState(false);
  const [mealtime,     setMealtime]    = useState('breakfast');
  const [isLoading,    setIsLoading]   = useState(false);
  const [lastEntries,  setLastEntries] = useState([]);
  const [historyQuery,   setHistoryQuery]   = useState('');
  const [historyResults, setHistoryResults] = useState(null); // null = keine Suche aktiv
  const [showSuccess,  setShowSuccess] = useState(false);
  const [savedEntry,   setSavedEntry]  = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [calorieGoal,  setCalorieGoal]  = useState(null);
  const [consumedToday, setConsumedToday] = useState(null);
  const { showToast } = useToast();

  const showMessage = (message, severity = 'success') =>
    showToast(message, { severity });

  // ── Letzte Einträge laden ──────────────────────────────────────────────────
  const fetchLastEntries = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await fetch('/api/quicktrack/last-entries', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setLastEntries(await res.json());
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchLastEntries(); }, [fetchLastEntries]);

  // ── Verlauf durchsuchen ─────────────────────────────────────────────────────
  useEffect(() => {
    const q = historyQuery.trim();
    if (!q) { setHistoryResults(null); return; }
    const token = localStorage.getItem('token');
    if (!token) return;
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`/api/quicktrack/search?query=${encodeURIComponent(q)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) setHistoryResults(await res.json());
      } catch (e) { console.error(e); }
    }, 300);
    return () => clearTimeout(timer);
  }, [historyQuery]);

  const displayedEntries = historyResults !== null ? historyResults : lastEntries;

  // ── Kalorien-Ziel & heutige Aufnahme laden ────────────────────────────────
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    Promise.all([
      fetch('/api/users/get-settings', { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.ok ? r.json() : null),
      fetch('/api/users/get-calories', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ date: selectedDate }),
      }).then((r) => r.ok ? r.json() : null),
    ]).then(([settings, calories]) => {
      if (settings?.calories) setCalorieGoal(Number(settings.calories));
      if (calories?.total_calories !== undefined) setConsumedToday(Number(calories.total_calories) || 0);
    }).catch(() => {});
  }, [selectedDate]);

  // ── Erfolg + Weiterleitung ─────────────────────────────────────────────────
  const triggerSuccess = (name, kcal) => {
    const hasCalData = calorieGoal !== null && consumedToday !== null;
    const prevRemaining = hasCalData ? calorieGoal - consumedToday : undefined;
    const newRemaining  = hasCalData ? calorieGoal - consumedToday - kcal : undefined;
    setSavedEntry({ name, kcal, prevRemaining, newRemaining });
    if (hasCalData) setConsumedToday((prev) => prev + kcal);
    setShowSuccess(true);
    const delay = isAnimEnabled() ? (hasCalData ? 2800 : 1700) : 200;
    setTimeout(() => navigate('/dashboard'), delay);
  };

  // ── Eintrag speichern (gemeinsam für Hauptformular + Wiederholen-Dialog) ───
  const submitQuickTrack = async (payload) => {
    const token = localStorage.getItem('token');
    if (!token) return false;
    try {
      const res = await fetch('/api/quicktrack/add', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ mealtime, date: selectedDate, ...payload }),
      });
      if (res.ok) {
        fetchLastEntries();
        triggerSuccess(payload.name, payload.kcal);
        return true;
      }
      showMessage('Fehler beim Speichern.', 'error');
      return false;
    } catch {
      showMessage('Verbindungsfehler.', 'error');
      return false;
    }
  };

  // ── Neuer Eintrag ──────────────────────────────────────────────────────────
  const finalKcal = mode === 'calc' ? computeKcal(unitType, refKcal, amount) : (totalKcal === '' ? null : parseInt(totalKcal, 10));

  const handleAdd = async () => {
    if (!foodName.trim() || finalKcal === null || !Number.isFinite(finalKcal)) return;

    setIsLoading(true);
    const ok = await submitQuickTrack({
      name: foodName.trim(), kcal: finalKcal,
      protein_g: foodProtein ? Number(foodProtein) : 0,
      fat_g: foodFat ? Number(foodFat) : 0,
      carbs_g: foodCarbs ? Number(foodCarbs) : 0,
      quantity: mode === 'calc' && amount ? Number(amount) : null,
      unit_label: mode === 'calc' ? unitType : null,
      kcal_per_unit: mode === 'calc' && refKcal ? Number(refKcal) : null,
    });
    if (ok) {
      setFoodName('');
      setRefKcal('');
      setAmount('');
      setTotalKcal('');
      setFoodProtein('');
      setFoodFat('');
      setFoodCarbs('');
    }
    setIsLoading(false);
  };

  // ── Schnellzugriff wiederholen ─────────────────────────────────────────────
  const [repeatTarget, setRepeatTarget] = useState(null);

  const handleRepeatSubmit = async (payload) => {
    const ok = await submitQuickTrack(payload);
    if (ok) setRepeatTarget(null);
    return ok;
  };

  // ── Eintrag löschen ────────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!deleteTarget) return;
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`/api/quicktrack/delete?id=${deleteTarget}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setLastEntries((prev) => prev.filter((i) => i.id !== deleteTarget));
        setHistoryResults((prev) => (prev ? prev.filter((i) => i.id !== deleteTarget) : prev));
      }
    } catch (e) { console.error(e); }
    setDeleteTarget(null);
  };

  const isFormValid = foodName.trim().length > 0 && finalKcal !== null && Number.isFinite(finalKcal);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      {showSuccess && (
        <SuccessOverlay entry={savedEntry} animate={isAnimEnabled()} />
      )}

      <Container maxWidth="sm" sx={{ pt: 3, pb: 14, display: 'flex', flexDirection: 'column', gap: 3 }}>

        {/* Header */}
        <PageHeader
          title="Schnelltracking"
          icon={<BoltOutlined fontSize="small" />}
          onBack={() => navigate('/dashboard')}
          dateChip={{ label: `📅 ${formatDate(selectedDate)}`, editable: false }}
          sx={{ mb: 0 }}
        />

        {/* Eingabekarte */}
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: radius.xl, overflow: 'hidden' }}>
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1.5,
            px: 2.5, py: 2,
            borderBottom: 1, borderColor: 'divider',
            bgcolor: 'action.hover',
          }}>
            <Typography variant="body1" fontWeight={700}>Eintrag erfassen</Typography>
          </Box>

          <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>

            {/* Name */}
            <TextField
              fullWidth
              label="Gerichtname"
              value={foodName}
              onChange={(e) => setFoodName(e.target.value)}
              autoComplete="off"
              slotProps={{ htmlInput: { autoCapitalize: 'sentences' } }}
            />

            <CalorieCalcFields
              mode={mode} setMode={setMode}
              unitType={unitType} setUnitType={setUnitType}
              refKcal={refKcal} setRefKcal={setRefKcal}
              amount={amount} setAmount={setAmount}
              totalKcal={totalKcal} setTotalKcal={setTotalKcal}
            />

            {/* Weitere Angaben (Protein/Fett/Carbs) — einklappbar */}
            <Button
              onClick={() => setShowMore((v) => !v)}
              endIcon={showMore ? <ExpandLess /> : <ExpandMore />}
              sx={{ alignSelf: 'flex-start', color: 'text.secondary' }}
            >
              Weitere Angaben
            </Button>
            <Collapse in={showMore} timeout="auto" unmountOnExit>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Protein in g (optional)"
                  value={foodProtein}
                  onChange={(e) => setFoodProtein(e.target.value.replace(/[^\d.,]/g, ''))}
                  onFocus={(e) => e.target.select()}
                  slotProps={{
                    htmlInput: { inputMode: 'decimal' },
                    input: {
                      endAdornment: <InputAdornment position="end">g</InputAdornment>,
                    },
                  }}
                />
                <TextField
                  fullWidth
                  label="Fett in g (optional)"
                  value={foodFat}
                  onChange={(e) => setFoodFat(e.target.value.replace(/[^\d.,]/g, ''))}
                  onFocus={(e) => e.target.select()}
                  slotProps={{
                    htmlInput: { inputMode: 'decimal' },
                    input: {
                      endAdornment: <InputAdornment position="end">g</InputAdornment>,
                    },
                  }}
                />
                <TextField
                  fullWidth
                  label="Carbs in g (optional)"
                  value={foodCarbs}
                  onChange={(e) => setFoodCarbs(e.target.value.replace(/[^\d.,]/g, ''))}
                  onFocus={(e) => e.target.select()}
                  slotProps={{
                    htmlInput: { inputMode: 'decimal' },
                    input: {
                      endAdornment: <InputAdornment position="end">g</InputAdornment>,
                    },
                  }}
                />
              </Box>
            </Collapse>

            {/* Mahlzeit-Chips */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {MEALS.map((m) => (
                <Chip
                  key={m.key}
                  label={`${m.emoji} ${m.label}`}
                  onClick={() => setMealtime(m.key)}
                  color={mealtime === m.key ? 'primary' : 'default'}
                  variant={mealtime === m.key ? 'filled' : 'outlined'}
                  sx={{ fontWeight: mealtime === m.key ? 700 : 400 }}
                />
              ))}
            </Box>

            {/* Erfassen-Button */}
            <Button
              variant="contained"
              fullWidth
              onClick={handleAdd}
              disabled={isLoading || !isFormValid}
              sx={{ py: 1.75, fontWeight: 700 }}
            >
              {isLoading
                ? <CircularProgress size={22} color="inherit" />
                : <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Check sx={{ fontSize: 20 }} />
                    Erfassen
                  </Box>
              }
            </Button>
          </Box>
        </Box>

        {/* Schnellzugriff */}
        {(lastEntries.length > 0 || historyQuery.trim()) && (
          <Box>
            <Typography variant="overline" sx={{
              color: 'text.secondary', fontWeight: 700,
              letterSpacing: 1.2, mb: 1.5, display: 'block',
            }}>
              Schnellzugriff
            </Typography>

            <TextField
              fullWidth
              size="small"
              placeholder="Verlauf durchsuchen …"
              value={historyQuery}
              onChange={(e) => setHistoryQuery(e.target.value)}
              autoComplete="off"
              sx={{ mb: 1.5 }}
            />

            {displayedEntries.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                Keine Treffer im Verlauf.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {displayedEntries.map((entry) => {
                  const meal = MEALS.find((m) => m.key === entry.mealtime);
                  const hasRef = entry.quantity != null && entry.unit_label;
                  const amountText = hasRef
                    ? ` · ${entry.quantity} ${entry.unit_label === 'portion' ? (entry.quantity === 1 ? 'Portion' : 'Portionen') : unitDisplay(entry.unit_label).replace('100 ', '')}`
                    : '';
                  return (
                    <ListRow
                      key={entry.id}
                      title={entry.product_name}
                      subtitle={`${meal?.emoji} ${meal?.label} · ${entry.kcal_all} kcal${amountText}`}
                      trailing={
                        <>
                          <IconButton size="small" onClick={() => setRepeatTarget(entry)} sx={{ color: 'primary.main' }}>
                            <Replay sx={{ fontSize: 18 }} />
                          </IconButton>
                          <IconButton size="small" onClick={() => setDeleteTarget(entry.id)} sx={{ color: 'text.disabled' }}>
                            <DeleteOutline sx={{ fontSize: 18 }} />
                          </IconButton>
                        </>
                      }
                    />
                  );
                })}
              </Box>
            )}
          </Box>
        )}

      </Container>

      {/* ── Dialog: Löschen bestätigen ──────────────────────────────────────── */}
      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Eintrag löschen?"
        message="Dieser Eintrag wird aus dem Schnellzugriff entfernt."
        confirmLabel="Löschen"
        destructive
      />

      {/* ── Dialog: Erneut Tracken ──────────────────────────────────────────── */}
      <RepeatEntryDialog
        open={Boolean(repeatTarget)}
        entry={repeatTarget}
        onClose={() => setRepeatTarget(null)}
        onSubmit={handleRepeatSubmit}
      />
    </>
  );
};

// ─── Wiederholen-Dialog ─────────────────────────────────────────────────────
// Übernimmt die beim letzten Mal gespeicherte Referenz (kcal pro 100g/100ml/
// Portion) bzw. den Gesamtwert — je nachdem, wie der Eintrag ursprünglich
// erfasst wurde — sodass hier meist nur noch die tatsächlich gegessene
// Menge neu eingetragen werden muss.
function RepeatEntryDialog({ open, entry, onClose, onSubmit }) {
  const [mode,      setMode]      = useState('calc');
  const [unitType,  setUnitType]  = useState('100g');
  const [refKcal,   setRefKcal]   = useState('');
  const [amount,    setAmount]    = useState('');
  const [totalKcal, setTotalKcal] = useState('');
  const [saving,    setSaving]    = useState(false);

  useEffect(() => {
    if (!open || !entry) return;
    const hasRef = entry.kcal_per_unit != null && UNIT_TYPES.some((u) => u.val === entry.unit_label);
    if (hasRef) {
      setMode('calc');
      setUnitType(entry.unit_label);
      setRefKcal(String(entry.kcal_per_unit));
      setAmount('');
    } else {
      setMode('total');
      setTotalKcal(String(entry.kcal_all ?? ''));
    }
  }, [open, entry]);

  if (!entry) return null;

  const finalKcal = mode === 'calc' ? computeKcal(unitType, refKcal, amount) : (totalKcal === '' ? null : parseInt(totalKcal, 10));
  const isValid = finalKcal !== null && Number.isFinite(finalKcal);

  const handleSubmit = async () => {
    setSaving(true);
    await onSubmit({
      name: entry.product_name,
      kcal: finalKcal,
      quantity: mode === 'calc' && amount ? Number(amount) : null,
      unit_label: mode === 'calc' ? unitType : null,
      kcal_per_unit: mode === 'calc' && refKcal ? Number(refKcal) : null,
      protein_g: Number(entry.protein_g) || 0,
      fat_g: Number(entry.fat_g) || 0,
      carbs_g: Number(entry.carbs_g) || 0,
    });
    setSaving(false);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ fontWeight: 700 }}>{entry.product_name}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        <CalorieCalcFields
          mode={mode} setMode={setMode}
          unitType={unitType} setUnitType={setUnitType}
          refKcal={refKcal} setRefKcal={setRefKcal}
          amount={amount} setAmount={setAmount}
          totalKcal={totalKcal} setTotalKcal={setTotalKcal}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={onClose} variant="outlined" sx={{ flex: 1 }} disabled={saving}>
          Abbrechen
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={!isValid || saving} sx={{ flex: 1 }}>
          {saving ? <CircularProgress size={20} color="inherit" /> : 'Erfassen'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default QuickTrackPage;
